import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Building2, 
  Package, 
  AlertTriangle,
  Calendar,
  Filter,
  RefreshCw,
  Download
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import axios from 'axios';
import toast from 'react-hot-toast';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsHub = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTimeRange, setActiveTimeRange] = useState('30d');
  const [selectedMetrics, setSelectedMetrics] = useState(['assets', 'incidents', 'maintenance']);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Fetch analytics data
  const fetchAnalyticsData = async () => {
    try {
      setRefreshing(true);
      const [dashboardStats, activities, engagementData] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/activities?limit=20`),
        axios.get(`${API}/employee-engagement/dashboard`).catch(() => ({ data: null }))
      ]);

      // Generate mock trend data for demonstration
      const trendData = generateTrendData(activeTimeRange);
      
      setAnalyticsData({
        stats: dashboardStats.data,
        activities: activities.data.activities || [],
        engagement: engagementData.data,
        trends: trendData
      });
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Generate trend data based on time range
  const generateTrendData = (timeRange) => {
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const labels = [];
    const assetData = [];
    const incidentData = [];
    const maintenanceData = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      labels.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      
      // Generate realistic trend data
      assetData.push(Math.floor(Math.random() * 20) + 80 + (i * 0.5));
      incidentData.push(Math.floor(Math.random() * 10) + 5);
      maintenanceData.push(Math.floor(Math.random() * 15) + 10);
    }

    return { labels, assetData, incidentData, maintenanceData };
  };

  // Handle export functionality
  const handleExportReport = async () => {
    try {
      setExporting(true);
      
      // Generate comprehensive analytics report
      let reportContent = 'Analytics Report\n';
      reportContent += `Generated: ${new Date().toLocaleString()}\n\n`;
      
      // Add dashboard statistics
      if (analyticsData?.stats) {
        reportContent += 'Dashboard Statistics\n';
        reportContent += '===================\n';
        Object.entries(analyticsData.stats).forEach(([key, value]) => {
          const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          reportContent += `${formattedKey}: ${value}\n`;
        });
        reportContent += '\n';
      }
      
      // Add recent activities summary
      if (analyticsData?.activities && analyticsData.activities.length > 0) {
        reportContent += 'Recent Activities Summary\n';
        reportContent += '========================\n';
        reportContent += `Total Activities: ${analyticsData.activities.length}\n`;
        
        // Group activities by type
        const activityGroups = analyticsData.activities.reduce((acc, activity) => {
          const type = activity.activity_type || 'unknown';
          acc[type] = (acc[type] || 0) + 1;
          return acc;
        }, {});
        
        Object.entries(activityGroups).forEach(([type, count]) => {
          const formattedType = type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          reportContent += `${formattedType}: ${count}\n`;
        });
        reportContent += '\n';
        
        // Add recent activity details
        reportContent += 'Recent Activity Details\n';
        reportContent += '======================\n';
        analyticsData.activities.slice(0, 10).forEach((activity, index) => {
          reportContent += `${index + 1}. ${activity.description || activity.activity_type}\n`;
          if (activity.created_at) {
            reportContent += `   Time: ${new Date(activity.created_at).toLocaleString()}\n`;
          }
        });
        reportContent += '\n';
      }
      
      // Add engagement data if available
      if (analyticsData?.engagement) {
        reportContent += 'Employee Engagement\n';
        reportContent += '==================\n';
        if (analyticsData.engagement.overall_engagement_score) {
          reportContent += `Overall Engagement Score: ${Math.round(analyticsData.engagement.overall_engagement_score)}%\n`;
        }
        reportContent += '\n';
      }
      
      // Create and download file
      const filename = `analytics_report_${new Date().toISOString().split('T')[0]}.txt`;
      const blob = new Blob([reportContent], { type: 'text/plain;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Analytics report exported successfully!');
      
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [activeTimeRange]);

  // Chart configurations
  const trendChartData = analyticsData ? {
    labels: analyticsData.trends.labels,
    datasets: [
      {
        label: 'Active Assets',
        data: analyticsData.trends.assetData,
        borderColor: '#00C5CB',
        backgroundColor: 'rgba(0, 197, 203, 0.1)',
        tension: 0.4,
        hidden: !selectedMetrics.includes('assets')
      },
      {
        label: 'Incidents',
        data: analyticsData.trends.incidentData,
        borderColor: '#EF4444',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        tension: 0.4,
        hidden: !selectedMetrics.includes('incidents')
      },
      {
        label: 'Maintenance Tasks',
        data: analyticsData.trends.maintenanceData,
        borderColor: '#F59E0B',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        tension: 0.4,
        hidden: !selectedMetrics.includes('maintenance')
      }
    ]
  } : null;

  const distributionChartData = analyticsData ? {
    labels: ['Active Assets', 'Inactive Assets', 'Pending Incidents', 'Service Logs'],
    datasets: [{
      data: [
        analyticsData.stats.active_assets,
        analyticsData.stats.inactive_assets || (analyticsData.stats.total_assets - analyticsData.stats.active_assets),
        analyticsData.stats.pending_incidents,
        analyticsData.stats.total_service_logs
      ],
      backgroundColor: ['#00C5CB', '#6B7280', '#EF4444', '#3B82F6'],
      borderWidth: 2,
      borderColor: '#ffffff'
    }]
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
      },
      y: {
        display: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics Overview</h2>
          <p className="text-gray-600">Cross-module analytics and performance insights</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          {/* Time Range Selector */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {['7d', '30d', '90d'].map((range) => (
              <button
                key={range}
                onClick={() => setActiveTimeRange(range)}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  activeTimeRange === range
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
              </button>
            ))}
          </div>

          {/* Refresh Button */}
          <button
            onClick={fetchAnalyticsData}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Asset Utilization</p>
              <p className="text-2xl font-bold text-gray-900">
                {analyticsData?.stats.active_assets && analyticsData?.stats.total_assets
                  ? Math.round((analyticsData.stats.active_assets / analyticsData.stats.total_assets) * 100)
                  : 0}%
              </p>
              <p className="text-sm text-green-600">+2.5% from last month</p>
            </div>
            <Package className="h-8 w-8 text-cyan-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Incident Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {analyticsData?.stats.pending_incidents || 0}
              </p>
              <p className="text-sm text-red-600">+1 from yesterday</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-red-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">User Engagement</p>
              <p className="text-2xl font-bold text-gray-900">
                {analyticsData?.engagement?.overall_engagement_score 
                  ? Math.round(analyticsData.engagement.overall_engagement_score)
                  : 85}%
              </p>
              <p className="text-sm text-green-600">+5.2% this week</p>
            </div>
            <Users className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Properties</p>
              <p className="text-2xl font-bold text-gray-900">
                {analyticsData?.stats.total_properties || 0}
              </p>
              <p className="text-sm text-gray-600">No change</p>
            </div>
            <Building2 className="h-8 w-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trend Analysis Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Trend Analysis</h3>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                multiple
                value={selectedMetrics}
                onChange={(e) => setSelectedMetrics(Array.from(e.target.selectedOptions, option => option.value))}
                className="text-sm border border-gray-300 rounded px-2 py-1"
              >
                <option value="assets">Assets</option>
                <option value="incidents">Incidents</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>
          </div>
          <div className="h-80">
            {trendChartData && (
              <Line data={trendChartData} options={chartOptions} />
            )}
          </div>
        </div>

        {/* Distribution Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Resource Distribution</h3>
          <div className="h-80">
            {distributionChartData && (
              <Doughnut data={distributionChartData} options={doughnutOptions} />
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity Summary */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Recent Activity Summary</h3>
          <button 
            onClick={handleExportReport}
            disabled={exporting}
            className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className={`h-4 w-4 ${exporting ? 'animate-pulse' : ''}`} />
            {exporting ? 'Exporting...' : 'Export Report'}
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">Asset Activities</h4>
            <p className="text-2xl font-bold text-cyan-600">
              {analyticsData?.activities.filter(a => a.activity_type.includes('asset')).length || 0}
            </p>
            <p className="text-sm text-gray-600">Last 24 hours</p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">User Logins</h4>
            <p className="text-2xl font-bold text-blue-600">
              {analyticsData?.activities.filter(a => a.activity_type === 'user_login').length || 0}
            </p>
            <p className="text-sm text-gray-600">Today</p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">System Events</h4>
            <p className="text-2xl font-bold text-purple-600">
              {analyticsData?.activities.filter(a => a.activity_type === 'system_event').length || 0}
            </p>
            <p className="text-sm text-gray-600">This week</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsHub;